<template>
  <div v-if="player" ref="playerCard" :class="['gam-player-result-row', getPlayerCardClasses]">
    <div :class="['avatar', { connected: isConnected }]">
      <img v-if="player.profileImageBlobHash" class="picture" loading="lazy" :src="imageSrc" alt="" />
      <div v-else class="picture missing">
        <GamIcon :name="GamIconName.PAWN_EMPTY" :size="GamIconSize.SMALL" />
      </div>
    </div>

    <div class="username-name-title">
      <div class="username-row">
        <span class="username">
          <span>@{{ player?.username || 'unknown' }}</span>
          <gam-icon v-if="player?.featured" class="featured" :name="GamIconSpecialName.BADGE_MATCH" size="14px" />
        </span>
      </div>

      <div class="name">
        <span class="first-name">{{ player.firstName }}</span> <span class="last-name">{{ player.lastName }}</span>
      </div>
    </div>

    <div class="rating-age-distance">
      <div class="chips">
        <GamPlayerRoles class="titles" :size="GamChipSize.SMALL" v-if="player" :player="player" show-all />

        <GamChip
          v-if="player.rating"
          :size="GamChipSize.SMALL"
          :left-icon="GamIconName.GAMBIT"
          :dots-rating="isDots ? player.rating : undefined"
          :label="isDots ? undefined : String(player.rating)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { HandshakeFields } from '@/core/data/user/user.interface';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import {
  GamIconName,
  GamIconSize,
  GamIconSpecialName,
} from '@/views/composables/constants/components/gamIcon.constants';
import type { GamPlayerCardType } from '@/views/composables/models/components/GamPlayer';
import { computed, ref, watch } from 'vue';
import GamPlayerRoles from './GamPlayerRoles.vue';

const props = defineProps<GamPlayerCardType>();

const playerCard = ref<HTMLDivElement>();

const isTitled = (): boolean => {
  return !!props.player?.title;
};

const getPlayerCardClasses = computed(() => {
  return { titled: isTitled(), selected: props.selected, 'is-mobile': isMobile.value, auto: props.autoSize };
});

const getHeight = computed(() => {
  return playerCard.value?.clientHeight;
});

const isDots = computed(() => props.player?.ratingDisplayMode === RatingDisplayMode.DOTS);

const imageSrc = computed((): string => {
  const url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`;
  return `${url.replace(new RegExp('{blobHash}', 'g'), props.player?.profileImageBlobHash || '')}?width=100`;
});

// Someone you are connected to keeps their colours; everyone else stays
// greyscale so your own people stand out while scrolling.
const isConnected = computed(() => {
  const isHandshakeConfirmed = (props.player as any as HandshakeFields)?.isHandshakeConfirmed;
  return Boolean(props.player?.isFollowed || props.player?.isFollowingMe || isHandshakeConfirmed);
});

watch(props, ({ selected }) => {
  if (!selected || isMobile.value) return;

  playerCard.value?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
});

defineExpose({
  getHeight,
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-player-result-row {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: nowrap;
  overflow: hidden;

  min-height: 5rem;
  width: 100%;
  padding: var(--spacing-md) var(--spacing-l);
  border: 1px solid var(--color-dark-700);

  @extend %tinted-card;

  &:before {
    background: var(--color-white-30);
    padding: 1px;
    opacity: 0;
  }

  &.selected {
    @extend %active-tinted-card;
    border-color: rgba(255, 255, 255, 0.18);

    &:before {
      opacity: 1;
    }
  }

  @include hover {
    background-color: var(--color-dark-500);
    box-shadow: var(--shadow-card-hover);
  }

  // &.titled {
  //   background: var(--color-radial-gradient);
  //   background-color: var(--color-dark-600);
  // }
}

.avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;

  .picture {
    width: 48px;
    height: 48px;
    object-fit: cover;
    overflow: hidden;
    border-radius: 48px !important;
    filter: saturate(0);
  }

  .picture.missing {
    background: var(--color-dark-70);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.connected .picture {
    filter: none;
  }
}

.username-name-title {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
  flex-grow: 1;
  overflow: hidden;
}

.username-row {
  height: 20px;
  display: flex;
  gap: var(--spacing-l);
}

.username {
  font-weight: 600;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.03em;
}

.name {
  display: flex;
  gap: var(--spacing-s);
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  font-size: 16px;
  font-weight: 400;
  color: var(--color-white-30);
  height: 20px;

  span {
    text-transform: capitalize;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .first-name {
    flex-shrink: 0;
  }

  .role {
    text-transform: uppercase;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 400;
  }

  .role-titled {
    color: var(--color-pink);
  }

  .role-coach {
    color: var(--color-yellow);
  }

  .role-streamer {
    color: var(--color-white-70);
  }

  .ratings {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    justify-content: center;
  }
}

.rating-age-distance {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  flex-shrink: 1;
  gap: var(--spacing-s);
  font-size: small;
  color: var(--color-white-30);

  .titles {
    gap: var(--spacing-s);
  }

  div {
    display: flex;
    align-items: center;
    // flex-wrap: wrap;
    justify-content: flex-end;
    text-align: right;
    line-height: 1.5;
    gap: var(--spacing-s);
  }
}

.chips {
  height: 20px;
}
</style>
